import React, { createContext, useContext, useState } from 'react';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';

import { useGetResult } from '@/hooks/useGetResultBackCompressiveForceEstimation';
import { useGetReport } from '@/hooks/useGetBackCompressiveForceEstimation';
import { useApplicationContext } from '@/context/v1/Application/context';
import { Informations } from './Report/Informations';
import { LoadingSkeleton } from './LoadingSkeleton';
import { Variables } from './Report/Variables';
import { Text } from '@/components/Typography';
import { Result } from './Report/Result';
import type {
	BackCompressiveForceEstimationProviderProps,
	BackCompressiveForceEstimationRouteParams,
	BackCompressiveForceEstimationReportDTO,
	InitialValuesMapperInterface,
	SystemOfUnitMeasurements,
	InitialValues,
	FormStep,
	Context,
	Methods,
	States
} from './types';
import { useSandboxExistsReport } from '@/hooks/useSandbox';
import { SendReportRequest } from '@/types/Sandbox';
import { useHistory } from 'react-router-dom';

const { useForm } = Form;
const BackCompressiveForceEstimationContext = createContext<Context>({} as Context);

const steps: FormStep[] = [
	{
		icon: null,
		title: 'Informations',
		component: <Informations />
	},
	{
		icon: null,
		title: 'Variables',
		component: <Variables />
	},
	{
		icon: null,
		title: 'Result',
		component: <Result />
	}
];

class InitialValuesMapper implements InitialValuesMapperInterface {
	constructor(private backCompressiveForceEstimation: BackCompressiveForceEstimationReportDTO | undefined) { }

	public mapInitialValues(): InitialValues | undefined {
		if (!this.backCompressiveForceEstimation) {
			return undefined;
		}

		const { comment, line_id, sector_id, company_id, workstation_id, system_of_units } =
			this.backCompressiveForceEstimation;

		return {
			line_id,
			comment,
			sector_id,
			company_id,
			workstation_id,
			system_of_units_id: system_of_units?.id,
			back_compressive_force_estimation_inputs:
				this.backCompressiveForceEstimation.back_compressive_force_estimation_inputs
		};
	}
}

const measurements: { imperial: SystemOfUnitMeasurements; metric: SystemOfUnitMeasurements } = {
	imperial: {
		mass: 'lb',
		length: 'in',
		decimal_separator: '.'
	},
	metric: {
		mass: 'kg',
		length: 'cm',
		decimal_separator: ','
	}
};

export function BackCompressiveForceEstimationProvider({ children }: BackCompressiveForceEstimationProviderProps) {
	const [form] = useForm();
	const history = useHistory();
	const { file_id } = useParams<BackCompressiveForceEstimationRouteParams>();
	const { organization, company, sendReport } = useApplicationContext();

	const [updatedBackCompressiveForceEstimation, setUpdatedBackCompressiveForceEstimation] = useState<
		BackCompressiveForceEstimationReportDTO | undefined
	>(undefined);


	const [isSendReportModalOpen, setIsSendReportModalOpen] = useState<boolean>(false);

	const {
		data: backCompressiveForceEstimation,
		isFetching: fetchingReport,
		isError: errorGettingReport
	} = useGetReport({
		organization_id: organization?.id,
		company_id: company?.id,
		file_id
	});
	const {
		data: backCompressiveForceEstimationResult,
		isInitialLoading: fetchingResult,
		isError: errorGettingResult
	} = useGetResult({
		organization_id: organization?.id,
		company_id: company?.id,
		file_id,
		finished: isReportFinished()
	});

	const initialValuesMapper = new InitialValuesMapper(backCompressiveForceEstimation);
	const initialValues = initialValuesMapper.mapInitialValues();

	function isReportFinished(): boolean | undefined {
		return !!(
			updatedBackCompressiveForceEstimation?.total_compressive_force_result ||
			backCompressiveForceEstimation?.total_compressive_force_result
		);
	}

	function handleUpdateReport(report: BackCompressiveForceEstimationReportDTO) {
		setUpdatedBackCompressiveForceEstimation(report);
	}

	function useExistsReport(
		isSandbox: boolean,
		file_id: string | undefined,
		company_id: string | undefined,
		organization_id: string | undefined
	): { exists: boolean, isLoading: boolean } {
		const { data, isLoading } = useSandboxExistsReport(
			{
				file_id: file_id ?? '',
				company_id: company_id ?? '',
				organization_id: organization_id ?? '',
				report_type: 'BACK_COMPRESSIVE'
			},
			{
				enabled: Boolean(isSandbox && file_id && company_id && organization_id),
			}
		);

		if (isLoading) return { exists: false, isLoading };

		return {
			exists: data?.exists ?? false,
			isLoading
		};
	}

	function handleCleanReport() {
		setUpdatedBackCompressiveForceEstimation(undefined);
		form.resetFields();
	}

	function handleSendReport(): void {
		const request: SendReportRequest = {
			report_type: 'BACK_COMPRESSIVE',
			id: backCompressiveForceEstimationResult.id || '',
			company_id: company?.id,
			organization_id: organization?.id
		};

		setIsSendReportModalOpen(false);
		handleCleanReport();
		sendReport(request, '/reporting');

		const currentPath = history.location.pathname;
		const cleanPath = currentPath.match(/(\/reporting\/back-compressive-force-estimation)/)?.[1];

		if (cleanPath && currentPath !== cleanPath) {
			history.replace(cleanPath);
		}
	}

	function handleCloseSendReportModal(): void {
		setIsSendReportModalOpen(false);
	}

	function handleOpenSendReportModal(): void {
		setIsSendReportModalOpen(true);
	}

	const loading = fetchingReport || fetchingResult;

	if (errorGettingResult) {
		return <Text>Oops... Something went wrong!</Text>;
	}

	if (loading) {
		return <LoadingSkeleton loading={loading} />;
	}

	const states: States = {
		steps,
		file_id,
		errorGettingReport,
		backCompressiveForceEstimationResult,
		gettingReport: loading,
		backCompressiveForceEstimation: updatedBackCompressiveForceEstimation ?? backCompressiveForceEstimation,
		measurements:
			measurements[
			(updatedBackCompressiveForceEstimation ?? backCompressiveForceEstimation)?.system_of_units?.name
			] || null,
		isSendReportModalOpen
	};

	const methods: Methods = {
		handleUpdateReport,
		useExistsReport,
		handleSendReport,
		handleCloseSendReportModal,
		handleOpenSendReportModal
	};

	const context: Context = {
		...states,
		...methods
	};

	return (
		<BackCompressiveForceEstimationContext.Provider value={context}>
			<Form form={form} initialValues={initialValues}>
				{children}
			</Form>
		</BackCompressiveForceEstimationContext.Provider>
	);
}

export function useBackCompressiveForceEstimationContext() {
	const context = useContext(BackCompressiveForceEstimationContext);
	return context;
}
